import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap';
import { saveFairSeason } from '../../ReduxToolkit/Reducers/fairManagement';
import { CATEGORY_OPTIONS, toCategoryOptions } from './options';

const toForm = (season) => ({
  season_name: season?.season_name || '',
  description: season?.description || '',
  categories: toCategoryOptions(season?.categories || []),
  start_date: season?.start_date ? season.start_date.slice(0, 10) : '',
  end_date: season?.end_date ? season.end_date.slice(0, 10) : '',
});

// Create a season (season = null) or edit one. The venue isn't editable from admin: a new season
// has none until the fair picks it in the app, and an edit leaves it as it is.
const FairSeasonModal = ({ open, organizationId, season, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(toForm(season));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(toForm(season));
  }, [open, season]);

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const isComplete =
    form.season_name.trim() &&
    form.description.trim() &&
    form.categories.length &&
    form.start_date &&
    form.end_date;

  const handleSave = async () => {
    setSaving(true);
    const saved = await dispatch(
      saveFairSeason(organizationId, {
        ...(season?.id && { id: season.id }),
        season_name: form.season_name.trim(),
        description: form.description.trim(),
        categories: form.categories.map((o) => o.value),
        start_date: form.start_date,
        end_date: form.end_date,
      })
    );
    setSaving(false);
    if (saved) {
      onSaved?.(saved);
      onClose();
    }
  };

  const venue = season?.fair_location?.formatted_address || season?.fair_location?.name;

  return (
    <Modal isOpen={open} toggle={saving ? undefined : onClose} centered size="lg">
      <ModalHeader toggle={saving ? undefined : onClose}>
        {season ? 'Edit season' : 'Create season'}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md={12}>
            <FormGroup>
              <Label>Season name</Label>
              <Input
                value={form.season_name}
                onChange={(e) => set('season_name', e.target.value)}
                placeholder="e.g. Winter season"
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup>
              <Label>Description</Label>
              <Input
                type="textarea"
                rows={3}
                maxLength={2000}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <FormGroup>
              <Label>Sourcing categories</Label>
              <Select
                isMulti
                options={CATEGORY_OPTIONS}
                value={form.categories}
                onChange={(value) => set('categories', value || [])}
                closeMenuOnSelect={false}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>Start date</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => set('start_date', e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>End date</Label>
              <Input
                type="date"
                min={form.start_date || undefined}
                value={form.end_date}
                onChange={(e) => set('end_date', e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
        <small className="text-muted">
          Venue: {venue || 'not set'}. Only the fair can set the venue, from its app.
        </small>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" outline onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleSave} disabled={saving || !isComplete}>
          {saving ? <Spinner size="sm" color="light" /> : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FairSeasonModal;
